import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import styles from './Navbar.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CheckCircle2, ChevronDown, CircleDollarSign, Gift, Instagram, Loader2, Unlink } from 'lucide-react';
import {
  disconnectInstagram,
  getBusinessPendingApplicants,
  getBusinessPendingDeliveries,
  getCreatorApplicationDecisions,
  getCreatorInvitations
} from '../../services/api';
import { beginInstagramConnect } from '../../utils/instagramConnect';
import Avatar from './Avatar';

const CREATOR_DECISIONS_SEEN_KEY = 'foth_creator_application_decisions_seen';
const CREATOR_DECISIONS_NOTIFIED_KEY = 'foth_creator_application_decisions_notified';
const NAVBAR_POLL_INTERVAL_MS = 60000;

const getDecisionKey = (decision) => `${decision.id}:${decision.status}:${decision.updated_at}`;

const readSeenDecisionMap = () => {
  try {
    return JSON.parse(localStorage.getItem(CREATOR_DECISIONS_SEEN_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

const readNotifiedDecisionMap = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CREATOR_DECISIONS_NOTIFIED_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

const markDecisionsNotified = (decisions) => {
  const notifiedMap = readNotifiedDecisionMap();
  decisions.forEach((decision) => {
    notifiedMap[getDecisionKey(decision)] = true;
  });
  sessionStorage.setItem(CREATOR_DECISIONS_NOTIFIED_KEY, JSON.stringify(notifiedMap));
};

const markDecisionsSeen = (decisions) => {
  const seenMap = readSeenDecisionMap();
  decisions.forEach((decision) => {
    seenMap[getDecisionKey(decision)] = true;
  });
  localStorage.setItem(CREATOR_DECISIONS_SEEN_KEY, JSON.stringify(seenMap));
};

const SUPPORTED_LANGUAGES = [
  { code: 'es', label: 'ES', title: 'Español' },
  { code: 'en', label: 'EN', title: 'English' },
  { code: 'pt', label: 'PT', title: 'Português' },
];

const Navbar = () => {
  const { currentUser, logout, isAuthenticated, refreshUser, subscriptionInfo } = useAuth();
  const { t, i18n } = useTranslation('common');
  const translationRef = useRef(t);
  translationRef.current = t;
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = (i18n.resolvedLanguage || i18n.language || 'es').split('-')[0].toLowerCase();

  const renderLangSwitcher = (locationKey = 'nav') => (
    <div
      className={styles.langSwitcher}
      role="group"
      aria-label={t('navbar.language_select', 'Seleccionar idioma')}
    >
      {SUPPORTED_LANGUAGES.map((lang) => {
        const isActive = currentLang === lang.code;
        return (
          <button
            key={`${locationKey}-${lang.code}`}
            type="button"
            onClick={() => i18n.changeLanguage(lang.code)}
            className={clsx(styles.langItem, isActive && styles.activeLang)}
            aria-label={lang.title}
            aria-pressed={isActive}
            title={lang.title}
          >
            {lang.label}
          </button>
        );
      })}
    </div>
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [createCollabDropdownOpen, setCreateCollabDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [creatorDecisionUpdates, setCreatorDecisionUpdates] = useState({ count: 0, target: null });
  const [pendingApplicants, setPendingApplicants] = useState({ count: 0, target: null });
  const [pendingDeliveries, setPendingDeliveries] = useState({ count: 0, target: null });
  const [instagramBusy, setInstagramBusy] = useState(false);
  const dropdownRef = useRef(null);
  const createCollabDropdownRef = useRef(null);
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const reduceMotion = useReducedMotion();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (createCollabDropdownRef.current && !createCollabDropdownRef.current.contains(event.target)) {
        setCreateCollabDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll effect: track direction and position
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Scrolled state (for background and transparency)
      setIsScrolled(currentScrollY > 50);

      // Visibility based on scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 150) {
        // Scrolling down and past threshold: hide
        setIsVisible(false);
      } else {
        // Scrolling up or at the top: show
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsDrawerOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    drawerRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isDrawerOpen]);

  // Route change effect: close everything
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsDrawerOpen(false);
    setDropdownOpen(false);
    setCreateCollabDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || !['creator', 'business'].includes(currentUser?.user_type)) {
      setPendingInvites(0);
      setCreatorDecisionUpdates({ count: 0, target: null });
      setPendingApplicants({ count: 0, target: null });
      setPendingDeliveries({ count: 0, target: null });
      return undefined;
    }

    let isMounted = true;
    const translate = translationRef.current;

    const fetchCreatorInvites = async ({ notify = false } = {}) => {
      try {
        const response = await getCreatorInvitations();
        const invitations = Array.isArray(response.data) ? response.data : response.data?.results || [];
        const count = invitations.filter(inv => inv.status === 'pending').length;
        const previous = Number(sessionStorage.getItem('foth_pending_invites') || '0');

        if (isMounted) setPendingInvites(count);

        if (notify && count > previous) {
          toast.info(count === 1
            ? translate('navbar.pending_invites_toast_one')
            : translate('navbar.pending_invites_toast_many', { count }));
        }

        sessionStorage.setItem('foth_pending_invites', String(count));
      } catch (error) {
        if (isMounted) setPendingInvites(0);
      }
    };

    const fetchBusinessDeliveries = async ({ notify = false } = {}) => {
      try {
        const response = await getBusinessPendingDeliveries();
        const count = Number(response.data?.count || 0);
        const first = response.data?.results?.[0] || null;
        const previous = Number(sessionStorage.getItem('foth_pending_deliveries') || '0');

        if (isMounted) setPendingDeliveries({ count, target: first });

        if (notify && count > previous) {
          toast.info(count === 1
            ? translate('navbar.pending_deliveries_toast_one')
            : translate('navbar.pending_deliveries_toast_many', { count }));
        }

        sessionStorage.setItem('foth_pending_deliveries', String(count));
      } catch (error) {
        if (isMounted) setPendingDeliveries({ count: 0, target: null });
      }
    };

    const fetchBusinessApplicants = async ({ notify = false } = {}) => {
      try {
        const response = await getBusinessPendingApplicants();
        const count = Number(response.data?.count || 0);
        const first = response.data?.results?.[0] || null;
        const previous = Number(sessionStorage.getItem('foth_pending_applicants') || '0');

        if (isMounted) setPendingApplicants({ count, target: first });

        if (notify && count > previous) {
          toast.info(count === 1
            ? translate('navbar.pending_applicants_toast_one')
            : translate('navbar.pending_applicants_toast_many', { count }));
        }

        sessionStorage.setItem('foth_pending_applicants', String(count));
      } catch (error) {
        if (isMounted) setPendingApplicants({ count: 0, target: null });
      }
    };

    const fetchCreatorDecisions = async ({ notify = false } = {}) => {
      try {
        const response = await getCreatorApplicationDecisions();
        const decisions = Array.isArray(response.data?.results) ? response.data.results : [];

        if (location.pathname.startsWith('/creator/applications')) {
          markDecisionsSeen(decisions);
          if (isMounted) setCreatorDecisionUpdates({ count: 0, target: null });
          return;
        }

        const seenMap = readSeenDecisionMap();
        const unseen = decisions.filter((decision) => !seenMap[getDecisionKey(decision)]);
        const notifiedMap = readNotifiedDecisionMap();
        const unnotified = unseen.filter((decision) => !notifiedMap[getDecisionKey(decision)]);
        const first = unseen[0] || null;

        if (isMounted) setCreatorDecisionUpdates({ count: unseen.length, target: first });

        if (notify && unnotified.length > 0) {
          if (unnotified.length === 1) {
            const decision = unnotified[0];
            const key = decision.status === 'accepted'
              ? 'navbar.creator_decision_toast_accepted'
              : 'navbar.creator_decision_toast_rejected';
            toast.info(translate(key, { title: decision.offer_title }));
          } else {
            toast.info(translate('navbar.creator_decision_toast_many', { count: unnotified.length }));
          }
          markDecisionsNotified(unnotified);
        }
      } catch (error) {
        if (isMounted) setCreatorDecisionUpdates({ count: 0, target: null });
      }
    };

    const refresh = () => {
      if (document.hidden) return;
      if (currentUser?.user_type === 'creator') {
        fetchCreatorInvites({ notify: true });
        fetchCreatorDecisions({ notify: true });
      }
      if (currentUser?.user_type === 'business') {
        fetchBusinessApplicants({ notify: true });
        fetchBusinessDeliveries({ notify: true });
      }
    };

    refresh();
    const interval = window.setInterval(refresh, NAVBAR_POLL_INTERVAL_MS);
    const handleVisibleRefresh = () => {
      if (!document.hidden) refresh();
    };

    window.addEventListener('focus', refresh);
    window.addEventListener('foth:notifications-refresh', refresh);
    document.addEventListener('visibilitychange', handleVisibleRefresh);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('foth:notifications-refresh', refresh);
      document.removeEventListener('visibilitychange', handleVisibleRefresh);
    };
  }, [currentUser?.user_type, isAuthenticated, location.pathname, currentLang]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const instagramProfile = currentUser?.user_type === 'business'
    ? currentUser?.business_profile
    : currentUser?.creator_profile;
  const showInstagramStatus = isAuthenticated && ['creator', 'business'].includes(currentUser?.user_type);
  const isInstagramConnected = Boolean(instagramProfile?.instagram_account_connected);
  const instagramUsername = currentUser?.user_type === 'business'
    ? instagramProfile?.instagram_account || instagramProfile?.ig_name
    : instagramProfile?.ig_account || instagramProfile?.ig_name;

  const handleInstagramConnect = () => {
    const returnTo = `${location.pathname}${location.search || ''}`;
    setDropdownOpen(false);
    setIsDrawerOpen(false);
    beginInstagramConnect(returnTo);
  };

  const handleInstagramDisconnect = async () => {
    if (!window.confirm(t('navbar.instagram_disconnect_confirm'))) return;

    try {
      setInstagramBusy(true);
      await disconnectInstagram();
      await refreshUser?.();
      toast.success(t('navbar.instagram_disconnected_toast'));
    } catch (error) {
      toast.error(t('navbar.instagram_disconnect_error'));
    } finally {
      setInstagramBusy(false);
    }
  };

  const renderInstagramStatusBlock = () => showInstagramStatus ? (
    <div className={styles.instagramStatusBlock}>
      <div className={styles.instagramStatusHeader}>
        <div className={styles.instagramStatusIcon}>
          {isInstagramConnected ? <CheckCircle2 size={16} /> : <Instagram size={16} />}
        </div>
        <div className={styles.instagramStatusCopy}>
          <span>{t('navbar.instagram_status_label')}</span>
          <strong>
            {isInstagramConnected
              ? t('navbar.instagram_connected')
              : t('navbar.instagram_not_connected')}
          </strong>
          {isInstagramConnected && instagramUsername && (
            <small>@{instagramUsername}</small>
          )}
        </div>
      </div>

      <button
        type="button"
        className={clsx(
          styles.instagramActionButton,
          isInstagramConnected && styles.isDisconnect
        )}
        onClick={isInstagramConnected ? handleInstagramDisconnect : handleInstagramConnect}
        disabled={instagramBusy}
      >
        {instagramBusy ? (
          <Loader2 size={14} className={styles.spin} />
        ) : isInstagramConnected ? (
          <Unlink size={14} />
        ) : (
          <Instagram size={14} />
        )}
        {isInstagramConnected
          ? t('navbar.instagram_disconnect')
          : t('navbar.instagram_connect')}
      </button>
    </div>
  ) : null;



  const navLinkClass = (path) =>
    `${styles.link} ${(location.pathname === path || (path !== '/' && path !== '/dashboard' && location.pathname.startsWith(path))) ? styles.active : ''}`;

  const dropdownItemClass = (path) =>
    clsx(
      styles.dropdownItem,
      (location.pathname === path || (path !== '/' && path !== '/dashboard' && location.pathname.startsWith(path))) && styles.active
    );

  const pendingDeliveriesPath = pendingDeliveries.target?.offer_id
    ? `/business/collabs/${pendingDeliveries.target.offer_id}/journey?step=deliverables${pendingDeliveries.target.creator_username ? `&creator=${encodeURIComponent(pendingDeliveries.target.creator_username)}` : ''}`
    : '/business/collabs';
  const pendingApplicantsPath = pendingApplicants.target?.offer_id
    ? `/business/collabs/${pendingApplicants.target.offer_id}/applications`
    : '/business/collabs';
  const creatorDecisionPath = creatorDecisionUpdates.target?.status === 'rejected'
    ? '/creator/applications?tab=past'
    : '/creator/applications';
  const invitedOfferIds = currentUser?.creator_profile?.invited_offer_ids || [];
  const isApprovedCreator = currentUser?.user_type === 'creator'
    && currentUser?.creator_profile?.admin_verified
    && currentUser?.creator_profile?.verification_status === 'approved';
  const isRestrictedInvitedCreator = currentUser?.user_type === 'creator'
    && !isApprovedCreator
    && (
      currentUser?.creator_profile?.has_invited_access
      || invitedOfferIds.length > 0
      || currentUser?.creator_profile?.invited_collab
    );
  const showBusinessCollabAvailability = isAuthenticated && currentUser?.user_type === 'business';
  const isUnlimitedCollabs = Boolean(subscriptionInfo?.unlimited_collabs);
  const availableCollabs = subscriptionInfo?.has_active_subscription
    ? Math.max(Number(subscriptionInfo?.available_collabs || 0), 0)
    : 0;
  const collabAvailabilityLabel = isUnlimitedCollabs
    ? t('navbar.collabs_available_unlimited')
    : t(`navbar.${availableCollabs === 1 ? 'collab_available_one' : 'collab_available_many'}`, { count: availableCollabs });
  const collabAvailabilityTitle = subscriptionInfo?.has_active_subscription
    ? t('navbar.collabs_available_title')
    : t('navbar.collabs_available_none_title');

  const renderBusinessCollabAvailability = (variant = 'desktop') => showBusinessCollabAvailability ? (
    <Link
      to="/business/subscriptions"
      className={clsx(styles.collabAvailabilityPill, variant === 'drawer' && styles.drawerCollabAvailability)}
      title={collabAvailabilityTitle}
    >
      <span className={styles.collabAvailabilityValue}>
        {isUnlimitedCollabs ? '∞' : availableCollabs}
      </span>
      <span className={styles.collabAvailabilityText}>
        {collabAvailabilityLabel}
      </span>
    </Link>
  ) : null;

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <>
      <nav 
        className={clsx(
          styles.navbar,
          isScrolled && styles.scrolled,
          !isVisible && styles.hidden,
          location.pathname === '/' && !isScrolled && styles.transparent
        )}
        data-scrolled={isScrolled}
      >
        <div className={styles.container}>

          {/* LOGO PRINCIPAL */}
          <Link to="/" className={styles.brand}>FOTH</Link>

          {/* NAVEGACIÓN DESKTOP */}
          <ul className={styles.navLinks}>
            {!isAuthenticated ? (
              <>
                <li className={styles.navItem}><Link to="/product" className={styles.link}>{t('navbar.product')}</Link></li>
                <li className={styles.navItem}><Link to="/brands" className={styles.link}>{t('navbar.brands')}</Link></li>
                <li className={styles.navItem}><Link to="/creators" className={styles.link}>{t('navbar.creators')}</Link></li>
              </>
            ) : (
              <>
                {!isRestrictedInvitedCreator && (
                  <li className={styles.navItem}><Link to="/dashboard" className={navLinkClass('/dashboard')}>{t('navbar.home')}</Link></li>
                )}
                {currentUser?.user_type === 'business' && (
                  <>
                    <li className={styles.navItem} ref={createCollabDropdownRef}>
                      <button
                        type="button"
                        className={clsx(
                          styles.link,
                          styles.createCollabTrigger,
                          (location.pathname === '/business/create-collab' || location.pathname === '/business/create-paid-collab') && styles.active
                        )}
                        onClick={() => setCreateCollabDropdownOpen((prev) => !prev)}
                        aria-expanded={createCollabDropdownOpen}
                        aria-haspopup="true"
                      >
                        <span>{t('navbar.new_collab')}</span>
                        <ChevronDown size={14} className={clsx(styles.dropdownChevron, createCollabDropdownOpen && styles.rotated)} />
                      </button>

                      <AnimatePresence>
                        {createCollabDropdownOpen && (
                          <motion.div
                            className={styles.collabTypeDropdown}
                            initial={{ opacity: 0, y: 8, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 6, scale: 0.96 }}
                            transition={reduceMotion ? { duration: 0 } : { duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
                          >
                            <Link
                              to="/business/create-collab"
                              className={clsx(styles.collabTypeItem, location.pathname === '/business/create-collab' && styles.selectedType)}
                              onClick={() => setCreateCollabDropdownOpen(false)}
                            >
                              <div className={clsx(styles.typeIconBox, styles.giftingIcon)}>
                                <Gift size={20} />
                              </div>
                              <div className={styles.typeContent}>
                                <div className={styles.typeHeader}>
                                  <strong className={styles.typeTitle}>{t('navbar.gifting_collab')}</strong>
                                  <span className={styles.typeTag}>{t('navbar.gifting_tag')}</span>
                                </div>
                                <p className={styles.typeDesc}>
                                  {t('navbar.gifting_desc')}
                                </p>
                              </div>
                            </Link>

                            <div className={styles.dropdownDivider} />

                            <Link
                              to="/business/create-paid-collab"
                              className={clsx(styles.collabTypeItem, location.pathname === '/business/create-paid-collab' && styles.selectedType)}
                              onClick={() => setCreateCollabDropdownOpen(false)}
                            >
                              <div className={clsx(styles.typeIconBox, styles.paidIcon)}>
                                <CircleDollarSign size={20} />
                              </div>
                              <div className={styles.typeContent}>
                                <div className={styles.typeHeader}>
                                  <strong className={styles.typeTitle}>{t('navbar.paid_collab')}</strong>
                                  <span className={clsx(styles.typeTag, styles.paidTag)}>{t('navbar.paid_tag')}</span>
                                </div>
                                <p className={styles.typeDesc}>
                                  {t('navbar.paid_desc')}
                                </p>
                              </div>
                            </Link>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </li>
                    <li className={styles.navItem}><Link to="/business/collabs" className={navLinkClass('/business/collabs')}>{t('navbar.my_collabs')}</Link></li>
                    {pendingApplicants.count > 0 && (
                      <li className={styles.navItem}>
                        <Link
                          to={pendingApplicantsPath}
                          className={navLinkClass(pendingApplicantsPath)}
                          title={pendingApplicants.target?.offer_title || t('navbar.pending_applicants_title')}
                        >
                          {t('navbar.pending_applicants')} <span className={styles.navBadge}>{pendingApplicants.count}</span>
                        </Link>
                      </li>
                    )}
                    {pendingDeliveries.count > 0 && (
                      <li className={styles.navItem}>
                        <Link
                          to={pendingDeliveriesPath}
                          className={navLinkClass('/business/deliveries')}
                        >
                          {t('navbar.deliveries')} <span className={styles.navBadge}>{pendingDeliveries.count}</span>
                        </Link>
                      </li>
                    )}
                    <li className={styles.navItem}><Link to="/business/creators/discovery" className={navLinkClass('/business/creators/discovery')}>{t('navbar.discover')}</Link></li>
                    <li className={styles.navItem}>
                      <Link to="/business/subscriptions" className={navLinkClass('/business/subscriptions')}>
                        {t('navbar.subscriptions', 'Suscripciones')}
                      </Link>
                    </li>
                  </>
                )}
                {currentUser?.user_type === 'creator' && !isRestrictedInvitedCreator && (
                  <>
                    <li className={styles.navItem}><Link to="/creator/feed" className={navLinkClass('/creator/feed')}>{t('navbar.explore')}</Link></li>
                    {creatorDecisionUpdates.count > 0 && (
                      <li className={styles.navItem}>
                        <Link
                          to={creatorDecisionPath}
                          className={navLinkClass('/creator/applications')}
                          title={creatorDecisionUpdates.target?.offer_title || t('navbar.creator_decisions_title')}
                        >
                          {t('navbar.creator_decisions')} <span className={styles.navBadge}>{creatorDecisionUpdates.count}</span>
                        </Link>
                      </li>
                    )}
                    {pendingInvites > 0 && (
                      <li className={styles.navItem}>
                        <Link to="/creator/applications?tab=invitations" className={navLinkClass('/creator/applications')}>
                          {t('navbar.invitations')} <span className={styles.navBadge}>{pendingInvites}</span>
                        </Link>
                      </li>
                    )}
                    <li className={styles.navItem}><Link to="/creator/applications" className={navLinkClass('/creator/applications')}>{t('navbar.my_applications')}</Link></li>
                    <li className={styles.navItem}><Link to="/creator/wallet" className={navLinkClass('/creator/wallet')}>{t('navbar.wallet')}</Link></li>
                  </>
                )}
                {isRestrictedInvitedCreator && (
                  <>
                    <li className={styles.navItem}>
                      <Link
                        to="/creator/invited"
                        className={navLinkClass('/creator/invited')}
                      >
                        {t('navbar.my_invitations')}
                      </Link>
                    </li>
                    <li className={styles.navItem}>
                      <Link
                        to="/creator/wallet"
                        className={navLinkClass('/creator/wallet')}
                      >
                        {t('navbar.wallet')}
                      </Link>
                    </li>
                  </>
                )}
              </>
            )}
          </ul>

          {/* ACCIONES DERECHA */}
          <div className={styles.navActions}>
            {renderBusinessCollabAvailability()}
            {renderLangSwitcher('nav')}
            {!isAuthenticated ? (
              <>
                <Link to="/login" className={styles.loginBtn}>{t('navbar.login')}</Link>
                <Link to="/register" className={styles.registerBtn}>{t('navbar.apply')}</Link>
              </>
            ) : (
              <div className={styles.navItem} ref={dropdownRef} style={{ position: 'relative' }}>
                <button className={styles.userBtn} onClick={() => setDropdownOpen(!dropdownOpen)}>
                  <div className={styles.avatar}>
                    <Avatar
                      size="xs"
                      src={currentUser?.display_image_url || currentUser?.profile_picture_url || currentUser?.profile_picture}
                      fallbackSrc={currentUser?.creator_profile?.ig_profile_picture_url || currentUser?.business_profile?.ig_profile_picture_url}
                      name={currentUser?.first_name || currentUser?.business_profile?.company_name || currentUser?.username}
                      alt={currentUser?.username}
                    />
                  </div>
                  <span className={styles.username}>{currentUser?.username}</span>
                </button>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      className={styles.dropdownMenu}
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={reduceMotion
                        ? { duration: 0 }
                        : { duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
                    >
                      <Link to={`/profile/${currentUser.username}`} className={dropdownItemClass(`/profile/${currentUser.username}`)} onClick={() => setDropdownOpen(false)}>{t('navbar.profile')}</Link>
                      <Link to="/edit-profile" className={dropdownItemClass('/edit-profile')} onClick={() => setDropdownOpen(false)}>{t('navbar.settings')}</Link>
                      {currentUser?.user_type === 'business' && (
                        <>
                          <Link to="/business/subscriptions" className={dropdownItemClass('/business/subscriptions')} onClick={() => setDropdownOpen(false)}>
                            {t('navbar.subscriptions', 'Suscripciones')}
                          </Link>
                          <Link to="/business/transactions" className={dropdownItemClass('/business/transactions')} onClick={() => setDropdownOpen(false)}>
                            {t('navbar.transactions', 'Historial de transacciones')}
                          </Link>
                        </>
                      )}
                      {renderInstagramStatusBlock()}
                      <div className={styles.divider} />
                      <Link to="/help" className={dropdownItemClass('/help')} onClick={() => setDropdownOpen(false)}>
                        {t('navbar.help', 'Centro de Ayuda')}
                      </Link>
                      <Link to="/terms" className={dropdownItemClass('/terms')} onClick={() => setDropdownOpen(false)}>
                        {t('navbar.terms', 'Términos y Privacidad')}
                      </Link>
                      <div className={styles.divider} />
                      <button onClick={handleLogout} className={`${styles.dropdownItem} ${styles.danger}`}>{t('navbar.logout')}</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* BOTÓN HAMBURGUESA (SÓLO MOBILE) */}
            <button
              ref={menuButtonRef}
              className={styles.toggleButton}
              onClick={() => setIsDrawerOpen(true)}
              aria-label={t('navbar.menu')}
              aria-expanded={isDrawerOpen}
              aria-controls="foth-mobile-navigation"
            >
              {t('navbar.menu')}
            </button>
          </div>
        </div>
      </nav>

      {/* DRAWER MÓVIL (FUERA DEL NAV) */}
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            <motion.div
              className={styles.drawerOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsDrawerOpen(false)}
            />

            <motion.div
              ref={drawerRef}
              id="foth-mobile-navigation"
              className={styles.mobileDrawer}
              role="dialog"
              aria-modal="true"
              aria-labelledby="foth-mobile-navigation-title"
              tabIndex="-1"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className={styles.drawerHeader}>
                <span className={styles.drawerBrand} id="foth-mobile-navigation-title">FOTH</span>
                <div className={styles.drawerActions}>
                  {renderLangSwitcher('drawer')}
                  <button
                    className={styles.closeBtn}
                    onClick={() => setIsDrawerOpen(false)}
                    aria-label={t('navbar.close')}
                  >
                    {t('navbar.close')}
                  </button>
                </div>
              </div>

              <div className={styles.drawerContent}>
                <div className={styles.drawerSection}>
                  <div className={styles.drawerLabel}>{t('navbar.menu_section')}</div>
                  {!isAuthenticated ? (
                    <>
                      <Link to="/product" className={styles.drawerLink}>{t('navbar.product')}</Link>
                      <Link to="/brands" className={styles.drawerLink}>{t('navbar.brands')}</Link>
                      <Link to="/creators" className={styles.drawerLink}>{t('navbar.creators')}</Link>
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard" className={styles.drawerLink}>{t('navbar.home')}</Link>
                      {currentUser?.user_type === 'business' && (
                        <>
                          {renderBusinessCollabAvailability('drawer')}
                          <div className={styles.drawerGroup}>
                            <div className={styles.drawerGroupLabel}>{t('navbar.new_collab')}</div>
                            <Link
                              to="/business/create-collab"
                              className={clsx(styles.drawerLink, styles.drawerSubLink, location.pathname === '/business/create-collab' && styles.active)}
                              onClick={() => setIsDrawerOpen(false)}
                            >
                              <div className={clsx(styles.typeIconBox, styles.giftingIcon, styles.drawerSmallIcon)}>
                                <Gift size={16} />
                              </div>
                              <div className={styles.drawerSubText}>
                                <strong>{t('navbar.gifting_collab')}</strong>
                                <small>{t('navbar.gifting_desc_short')}</small>
                              </div>
                            </Link>
                            <Link
                              to="/business/create-paid-collab"
                              className={clsx(styles.drawerLink, styles.drawerSubLink, location.pathname === '/business/create-paid-collab' && styles.active)}
                              onClick={() => setIsDrawerOpen(false)}
                            >
                              <div className={clsx(styles.typeIconBox, styles.paidIcon, styles.drawerSmallIcon)}>
                                <CircleDollarSign size={16} />
                              </div>
                              <div className={styles.drawerSubText}>
                                <strong>{t('navbar.paid_collab')}</strong>
                                <small>{t('navbar.paid_desc_short')}</small>
                              </div>
                            </Link>
                          </div>
                          <Link to="/business/collabs" className={styles.drawerLink}>{t('navbar.my_collabs')}</Link>
                          {pendingApplicants.count > 0 && (
                            <Link to={pendingApplicantsPath} className={styles.drawerLink}>
                              {t('navbar.pending_applicants')} <span className={styles.navBadge}>{pendingApplicants.count}</span>
                            </Link>
                          )}
                          {pendingDeliveries.count > 0 && (
                            <Link to={pendingDeliveriesPath} className={styles.drawerLink}>
                              {t('navbar.deliveries')} <span className={styles.navBadge}>{pendingDeliveries.count}</span>
                            </Link>
                          )}
                          <Link to="/business/creators/discovery" className={styles.drawerLink}>{t('navbar.discover')}</Link>
                          <Link to="/business/subscriptions" className={styles.drawerLink}>{t('navbar.subscriptions', 'Suscripciones')}</Link>
                        </>
                      )}
                      {currentUser?.user_type === 'creator' && (
                        <>
                          {!isRestrictedInvitedCreator ? (
                            <>
                              <Link to="/creator/feed" className={styles.drawerLink}>{t('navbar.explore')}</Link>
                              {creatorDecisionUpdates.count > 0 && (
                                <Link to={creatorDecisionPath} className={styles.drawerLink}>
                                  {t('navbar.creator_decisions')} <span className={styles.navBadge}>{creatorDecisionUpdates.count}</span>
                                </Link>
                              )}
                              {pendingInvites > 0 && (
                                <Link to="/creator/applications?tab=invitations" className={styles.drawerLink}>
                                  {t('navbar.invitations')} <span className={styles.navBadge}>{pendingInvites}</span>
                                </Link>
                              )}
                              <Link to="/creator/applications" className={styles.drawerLink}>{t('navbar.my_applications')}</Link>
                              <Link to="/creator/wallet" className={styles.drawerLink}>{t('navbar.wallet')}</Link>
                            </>
                          ) : (
                            <>
                              <Link to="/creator/invited" className={styles.drawerLink}>{t('navbar.my_invitations')}</Link>
                              <Link to="/creator/wallet" className={styles.drawerLink}>{t('navbar.wallet')}</Link>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                {isAuthenticated && (
                  <div className={styles.drawerSection}>
                    <div className={styles.drawerLabel}>{t('navbar.account_section')}</div>
                    <Link to={`/profile/${currentUser.username}`} className={styles.drawerLink}>{t('navbar.my_profile')}</Link>
                    <Link to="/edit-profile" className={styles.drawerLink}>{t('navbar.settings')}</Link>
                    {currentUser?.user_type === 'business' && (
                      <>
                        <Link to="/business/subscriptions" className={styles.drawerLink}>
                          {t('navbar.subscriptions', 'Suscripciones')}
                        </Link>
                        <Link to="/business/transactions" className={styles.drawerLink}>
                          {t('navbar.transactions', 'Historial de transacciones')}
                        </Link>
                      </>
                    )}
                    {renderInstagramStatusBlock()}
                    <div className={styles.divider} />
                    <Link to="/help" className={styles.drawerLink} onClick={() => setIsDrawerOpen(false)}>
                      {t('navbar.help', 'Centro de Ayuda')}
                    </Link>
                    <Link to="/terms" className={styles.drawerLink} onClick={() => setIsDrawerOpen(false)}>
                      {t('navbar.terms', 'Términos y Privacidad')}
                    </Link>
                    <div className={styles.divider} />
                    <button onClick={handleLogout} className={`${styles.drawerLink} ${styles.drawerLogout}`}>
                      {t('navbar.logout')}
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.drawerFooter}>
                {!isAuthenticated ? (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Link to="/login" className={styles.loginBtn} style={{ flex: 1, justifyContent: 'center' }}>{t('navbar.login')}</Link>
                    <Link to="/register" className={styles.registerBtn} style={{ flex: 1, justifyContent: 'center' }}>{t('navbar.apply')}</Link>
                  </div>
                ) : (
                  <div className={styles.drawerUserBox}>
                    <div className={styles.avatar}>
                      <Avatar
                        size="md"
                        src={currentUser?.display_image_url || currentUser?.profile_picture_url || currentUser?.profile_picture}
                        fallbackSrc={currentUser?.creator_profile?.ig_profile_picture_url || currentUser?.business_profile?.ig_profile_picture_url}
                        name={currentUser?.first_name || currentUser?.business_profile?.company_name || currentUser?.username}
                        alt={currentUser?.username}
                      />
                    </div>
                    <div className={styles.userInfo}>
                      <span className={styles.name}>{currentUser?.username}</span>
                      <span className={styles.type}>{currentUser?.user_type === 'business' ? t('navbar.user_type_business') : t('navbar.user_type_creator')}</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
